// Copy for /content (listing of case studies and blog posts).
// Card copy is keyed by the item's slug; the item list itself (order, type, slug) lives in src/pages/Content.tsx.
// Target pages (case studies, blog posts) have their own copy files (caseStudy*.ts, blog*.ts) — add new ones to SK_PATHS too.

type ContentItemCopy = {
  title: string;
  client: string;
  industry: string;
  description: string;
  tags: string[];
  stats: string[];
  year: string;
};

const en = {
  seoTitle: 'Content & Case Studies | Stratodata',
  seoDescription: 'Real projects, honest write-ups. Case studies and articles about retail data engineering, automation and AI analytics from the Stratodata team.',
  hero: {
    eyebrow: 'Knowledge Hub',
    title: 'Content',
    subtitle: 'Real projects. Honest write-ups. What we built, how we built it, and what we learned.',
  },
  typeLabels: {
    all: 'All',
    'case-study': 'Case Study',
    blog: 'Blog',
  },
  readArticle: 'Read article',
  readCaseStudy: 'Read case study',
  empty: 'Nothing here yet — check back soon.',
  items: {
    '/blog/the-problem-with-ecommerce': {
      title: 'The Problem with E-commerce',
      client: 'Stratodata Team',
      industry: 'Retail Profitability',
      description: 'Revenue keeps growing, the dashboards look fine, and then the financial results arrive. Why e-commerce businesses so often discover a loss too late, and how to see it in time.',
      tags: ['E-commerce', 'Margin', 'Profitability', 'Reporting'],
      stats: ['35% gross margin', '10% left before OPEX'],
      year: 'Updated Oct 2026',
    },
    '/case-studies/altisport': {
      title: 'Revenue in 9 Markets Was Easy to See. Profit Wasn\'t. Until Now.',
      client: 'Altisport',
      industry: 'Sportswear & Outdoor Retail',
      description: 'How Altisport learned which countries, channels and products actually make money after marketing, delivery and returns, and now builds its own AI reports on top of those numbers.',
      tags: ['Vario', 'Google Analytics 4', 'Google BigQuery', 'dbt', 'Claude AI'],
      stats: ['9 markets', '9–10k SKUs', 'daily refresh'],
      year: '2026–Ongoing',
    },
    '/case-studies/herohero': {
      title: 'From Manual Reports to Real-Time Creator Data',
      client: 'HeroHero',
      industry: 'Creator Economy',
      description: 'How HeroHero doubled subscriptions while replacing gut feel with daily data on 10,000+ creators.',
      tags: ['Stripe', 'Google Analytics', 'Google BigQuery', 'dbt', 'Python', 'Google Data Studio'],
      stats: ['2× subscriptions', '10,000+ creators', 'seconds to answers'],
      year: '2025–Ongoing',
    },
    '/case-studies/alasans': {
      title: 'From Fragmented Spreadsheets to a Company-Wide Data Culture',
      client: 'Alasans',
      industry: 'International E-commerce',
      description: 'How we built an Azure data warehouse connecting 40+ platforms across 25 markets — and turned a reporting project into a platform for intelligent automation.',
      tags: ['Data Warehouse', 'Azure', 'Power BI', 'Automation'],
      stats: ['>80% growth', '100+ sources', '75K+ SKUs'],
      year: '2020–Ongoing',
    },
    '/case-studies/dcm': {
      title: 'From Manual Price Surveys to a Digital Price Statistics Pipeline',
      client: 'Statistical Office of Slovakia',
      industry: 'Public Sector / Price Statistics',
      description: 'Building a fully automated digital price data pipeline for Slovakia\'s national CPI — covering 19 product groups, 3 data sources, and 10+ Eurostat-aligned index methods.',
      tags: ['AWS', 'Python', 'SAS', 'Machine Learning', 'Public Sector'],
      stats: ['19 product groups', '3 data sources', '10+ index methods'],
      year: '2022–2023',
    },
  } as Record<string, ContentItemCopy>,
};

const sk: typeof en = {
  seoTitle: 'Obsah a prípadové štúdie | Stratodata',
  seoDescription: 'Skutočné projekty, opísané na rovinu. Prípadové štúdie a články tímu Stratodata o dátovom inžinierstve, automatizácii a AI analytike pre retail.',
  hero: {
    eyebrow: 'Centrum znalostí',
    title: 'Obsah',
    subtitle: 'Skutočné projekty, opísané na rovinu. Čo sme postavili, ako sme to urobili a čo sme sa pri tom naučili.',
  },
  typeLabels: {
    all: 'Všetko',
    'case-study': 'Prípadová štúdia',
    blog: 'Blog',
  },
  readArticle: 'Prečítať článok',
  readCaseStudy: 'Prečítať prípadovú štúdiu',
  empty: 'Zatiaľ tu nič nie je. Čoskoro pribudne.',
  items: {
    '/blog/the-problem-with-ecommerce': {
      title: 'Problém e-commerce',
      client: 'Tím Stratodata',
      industry: 'Ziskovosť v retaile',
      description: 'Tržby rastú, dashboardy vyzerajú dobre a potom prídu finančné výsledky. Prečo e-shopy tak často zistia stratu, až keď je neskoro, a ako ju odhaliť včas.',
      tags: ['E-commerce', 'Marža', 'Ziskovosť', 'Reporting'],
      stats: ['35 % hrubá marža', '10 % zostáva pred OPEX'],
      year: 'Aktualizované okt. 2026',
    },
    '/case-studies/altisport': {
      title: 'Tržby v 9 krajinách boli jasné. Zisk nie. Až doteraz.',
      client: 'Altisport',
      industry: 'Športové a outdoorové oblečenie',
      description: 'Ako Altisport zistil, ktoré krajiny, kanály a produkty naozaj zarábajú po odpočítaní marketingu, dopravy a vratiek. Dnes si nad týmito číslami stavia vlastné AI reporty.',
      tags: ['Vario', 'Google Analytics 4', 'Google BigQuery', 'dbt', 'Claude AI'],
      stats: ['9 trhov', '9–10 tis. SKU', 'denná aktualizácia'],
      year: '2026–dodnes',
    },
    '/case-studies/herohero': {
      title: 'Od ručných reportov k dátam o tvorcoch v reálnom čase',
      client: 'HeroHero',
      industry: 'Creator economy',
      description: 'Ako HeroHero zdvojnásobil počet predplatných a namiesto pocitu sa rozhoduje podľa denných dát o viac ako 10 000 tvorcoch.',
      tags: ['Stripe', 'Google Analytics', 'Google BigQuery', 'dbt', 'Python', 'Google Data Studio'],
      stats: ['2× viac predplatných', '10 000+ tvorcov', 'odpovede za pár sekúnd'],
      year: '2025–dodnes',
    },
    '/case-studies/alasans': {
      title: 'Od roztrieštených tabuliek k dátovej kultúre v celej firme',
      client: 'Alasans',
      industry: 'Medzinárodný e-commerce',
      description: 'Ako sme postavili dátový sklad v Azure, ktorý prepája viac ako 40 platforiem v 25 krajinách. Z reportingového projektu sa tak stala platforma pre inteligentnú automatizáciu.',
      tags: ['Dátový sklad', 'Azure', 'Power BI', 'Automatizácia'],
      stats: ['>80 % rast', '100+ zdrojov', '75 tis.+ SKU'],
      year: '2020–dodnes',
    },
    '/case-studies/dcm': {
      title: 'Od ručného zisťovania cien k digitálnej cenovej štatistike',
      client: 'Štatistický úrad SR',
      industry: 'Verejný sektor / cenová štatistika',
      description: 'Pre národný index spotrebiteľských cien (CPI) sme postavili plne automatizovaný tok digitálnych cenových dát. Pokrýva 19 skupín produktov, 3 dátové zdroje a viac ako 10 indexových metód v súlade s Eurostatom.',
      tags: ['AWS', 'Python', 'SAS', 'Strojové učenie', 'Verejný sektor'],
      stats: ['19 skupín produktov', '3 dátové zdroje', '10+ indexových metód'],
      year: '2022–2023',
    },
  },
};

export const contentCopy = { en, sk };
